'use client'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { FileText, Sparkles, Zap, CheckCircle2, ListChecks, DollarSign } from 'lucide-react'
import { motion } from 'framer-motion'
import { useTheme } from 'next-themes'
import { cn } from '@/lib/utils'

interface TechStackSectionProps {
  isMobile?: boolean
}

const aiTools = [
  {
    icon: FileText,
    title: "AI Proposal Generator",
    description: "Generate compelling, compliant proposals grounded in the actual solicitation documents. Our AI reads the RFP, understands the requirements, and produces tailored responses aligned with FAR/DFARS clauses and your company's past performance.",
    models: ['Multi-Model AI'],
    gradient: 'from-blue-500 to-cyan-500',
    features: [
      'Solicitation-aware generation',
      'Multiple AI models to choose from',
      'Tone, length, and template customization',
      'Grounded in your company knowledge base',
      'Export-ready formatting'
    ],
    color: "#0ea5e9",
    shadowColor: "rgba(14, 165, 233, 0.25)"
  },
  {
    icon: ListChecks,
    title: "Bid Requirement Extraction",
    description: "Automatically extract every requirement, deliverable, and compliance item from solicitation documents. Our AI parses RFPs, SOWs, and amendments to build a structured checklist so nothing falls through the cracks.",
    models: ['Document AI'],
    gradient: 'from-amber-500 to-orange-500',
    features: [
      'Auto-parse RFPs, SOWs, and amendments',
      'Structured requirement checklists',
      'Priority tagging (high, medium, low)',
      'Source citations from original documents',
      'Tracks compliance across bid lifecycle'
    ],
    color: "#f59e0b",
    shadowColor: "rgba(245, 158, 11, 0.25)"
  },
  {
    icon: Sparkles,
    title: "AI Coaching & Strategy",
    description: "Get a dedicated AI coach that analyzes your business profile, reviews your documents, and delivers personalized recommendations to strengthen your competitive position and win more contracts.",
    models: ['AI Coaching'],
    gradient: 'from-emerald-600 to-teal-600',
    features: [
      'Personalized strategy recommendations',
      'Document quality scoring & gap analysis',
      'Market positioning guidance',
      'Readiness assessments & action plans',
      'Progress tracking over time'
    ],
    color: "#10b981",
    shadowColor: "rgba(16, 185, 129, 0.25)"
  },
  {
    icon: DollarSign,
    title: "Pricing Intelligence",
    description: "Analyze historical award data, benchmark against competitors, and generate data-driven pricing strategies. Our AI evaluates past contract values to help you price competitively and maximize win probability.",
    models: ['Award Analytics'],
    gradient: 'from-violet-500 to-purple-600',
    features: [
      'Historical award data analysis',
      'Competitor pricing benchmarks',
      'Price-to-win modeling',
      'Cost volume guidance',
      'Market rate intelligence'
    ],
    color: "#8b5cf6",
    shadowColor: "rgba(139, 92, 246, 0.25)"
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: { 
    opacity: 1, 
    y: 0, 
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15
    }
  },
};

export function TechStackSection({ isMobile = false }: TechStackSectionProps) {
  const { resolvedTheme } = useTheme()
  const isDark = resolvedTheme === 'dark'
  
  return (
    <section className="mb-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="text-center mb-12"
      >
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-4">
          <Zap className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium text-primary">AI-Powered Platform</span>
        </div>
        <h3 className="text-3xl md:text-4xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-sky-400 via-blue-500 to-blue-600">
          Proposals, Requirements & Coaching
        </h3>
        <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
          From extracting every requirement in a solicitation to generating ready-to-submit proposals and 
          receiving personalized coaching—AI tools built for every stage of your bid process.
        </p>
      </motion.div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-2 gap-6"
      >
        {aiTools.map((tool, index) => {
          const Icon = tool.icon;
          return (
            <motion.div
              key={index}
              variants={itemVariants}
            >
              <Card 
                className={cn(
                  "transition-all duration-300 group flex flex-col h-full overflow-hidden border",
                  isMobile 
                    ? "bg-slate-800/90 border-slate-700/50" 
                    : "backdrop-blur-xl border-white/20 dark:border-white/10 bg-white/5 dark:bg-black/10 hover:shadow-2xl hover:scale-[1.02]"
                )}
                style={{ 
                  '--tool-color': tool.color, 
                  '--tool-shadow-color': tool.shadowColor,
                  boxShadow: isMobile 
                    ? '0 2px 10px rgba(0, 0, 0, 0.2)'
                    : `0 4px 16px 0 rgba(31, 38, 135, 0.08), 0 0 0 1px rgba(255, 255, 255, 0.08) inset`,
                } as React.CSSProperties}
              >
                <div className="w-full h-1 bg-gradient-to-r" style={{ 
                  background: `linear-gradient(to right, ${tool.color}, ${tool.shadowColor})` 
                }}></div>
                
                <div className="flex flex-col h-full p-6 transition-all duration-300 hover:bg-[var(--tool-color)]/5">
                  <CardHeader className="flex-row items-start gap-4 p-0 mb-4">
                    <div className={cn(
                      "p-3 rounded-xl bg-gradient-to-br flex-shrink-0 transition-all duration-300 group-hover:scale-110",
                      isDark 
                        ? `bg-gradient-to-br ${tool.gradient}`
                        : tool.gradient === 'from-blue-500 to-cyan-500'
                        ? 'bg-gradient-to-br from-blue-100 to-cyan-100'
                        : tool.gradient === 'from-amber-500 to-orange-500'
                        ? 'bg-gradient-to-br from-amber-100 to-orange-100'
                        : tool.gradient === 'from-violet-500 to-purple-600'
                        ? 'bg-gradient-to-br from-violet-100 to-purple-100'
                        : 'bg-gradient-to-br from-emerald-100 to-teal-100'
                    )}>
                      <Icon className={cn(
                        "w-6 h-6",
                        isDark 
                          ? "text-white"
                          : tool.gradient === 'from-blue-500 to-cyan-500'
                          ? "text-blue-600"
                          : tool.gradient === 'from-amber-500 to-orange-500'
                          ? "text-amber-600"
                          : tool.gradient === 'from-violet-500 to-purple-600'
                          ? "text-violet-600"
                          : "text-emerald-600"
                      )} />
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-xl text-primary mb-2">{tool.title}</CardTitle>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {tool.models.map((model, idx) => (
                          <span
                            key={idx}
                            className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary border border-primary/20"
                          >
                            {model}
                          </span>
                        ))}
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="flex-grow p-0 mb-4">
                    <CardDescription className="text-muted-foreground text-sm leading-relaxed mb-4">
                      {tool.description}
                    </CardDescription>
                    
                    {/* Features List */}
                    <div className="space-y-2">
                      {tool.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-sm">
                          <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                          <span className="text-muted-foreground">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </div>
              </Card>
            </motion.div>
          );
        })}
      </motion.div>
    </section>
  )
}
